var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
//#region Imports
import React, { useRef, useState } from "react";
import { Col, Modal, ModalBody, Row, Spinner, Toast } from "react-bootstrap";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import { useAppState } from "shared/AppState/app-state";
import { ButtonStyled, ModalClose, ModalMainHeader } from "shared/DesignSpecs/components/FormComponents";
import FormWrapper from "shared/components/FormWrapper";
import styled from 'styled-components';
import * as constants from "shared/constants/App";
import { PatternFormat } from "react-number-format";
import * as helperFunctions from 'shared/utils/HelperFunctions';
import { useAssignVendorContactToOrderMutation, useVerifyContactEmailMutation, useUpdateAndVerifyVendorApprovalContactMutation, useCreateAndVerifyVendorApprovalContactMutation } from 'shared/types/__generated__/graphql-type';
import { connect } from "react-redux";
import { getApolloErrorMessage } from "shared/utils/HelperFunctionsTS";
import { event } from 'shared/services/GoogleAnalytics';
//#endregion
// #region Styled Components
const ModalStyled = styled(Modal).attrs({
    dialogClassName: "childStyle"
}) `
.childStyle {
  width: 900px;
  max-width: 900px;
  min-height: 600px;
}
.childStyle > div > div {
  min-height: 10em;
}`;
const ModalContent = styled(Row) `
  padding: 1rem 3rem 3rem 3rem;
`;
const CodeInput = styled(PatternFormat) `
  height: 12rem;
  width: 8rem;
  font-size: 10rem;
  text-align: center;
  background-color: ${props => props.loading ? "#ddd" : "white"}
`;
const InputCol = styled(Col) `
  margin-right: 5rem;
`;
const Line = styled(Row) `
  border-left: 6px solid #bee093;
  height: calc(100% - 7rem);
  margin-top: 1rem;
`;
const ResendToast = styled(Toast) `
  width: 100%;
  box-shadow: none;
  font-size: 16px;
  text-align: center;
`;
//#endregion
//#region Component
const ContactVerificationModal = (props) => {
    var _a;
    // #region State
    const [errorMessages, setErrorMessages] = useState([]);
    const [verificationCode, setVerificationCode] = useState(['', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState(null);
    // #endregion
    // #region Ref
    const input1 = useRef(null);
    const input2 = useRef(null);
    const input3 = useRef(null);
    const input4 = useRef(null);
    // #endregion
    // #region Hooks
    const intl = useIntl();
    const [appState, dispatch] = useAppState();
    const [assignVendorContactToOrderMutation, { data: assignData, loading: assignLoading, error: assignError }] = useAssignVendorContactToOrderMutation({
        client: props.vendorClient,
        onError(error) {
            setErrorMessages(getApolloErrorMessage(error));
        },
        onCompleted() {
            setErrorMessages([]);
        }
    });
    const [verifyContactEmailMutation, { data: verifyData, loading: verifyLoading, error: verifyError }] = useVerifyContactEmailMutation({
        client: props.vendorClient,
        onError(error) {
            setErrorMessages(getApolloErrorMessage(error));
        },
        onCompleted() {
            setErrorMessages([]);
        }
    });
    const [updateAndVerifyVendorApprovalContactMutation, { data: updateVerifyData, loading: updateVerifyLoading, error: updateVerifyError }] = useUpdateAndVerifyVendorApprovalContactMutation({
        client: props.vendorClient,
        onError(error, clientOptions) {
            setErrorMessages(getApolloErrorMessage(error));
        },
        onCompleted() {
            setErrorMessages([]);
        }
    });
    const [createAndVerifyVendorApprovalContactMutation, { data: createVerifyData, loading: createVerifyLoading, error: createVerifyError }] = useCreateAndVerifyVendorApprovalContactMutation({
        client: props.vendorClient,
        onError(error, clientOptions) {
            setErrorMessages(getApolloErrorMessage(error));
        },
        onCompleted() {
            setErrorMessages([]);
        }
    });
    // #endregion
    // #region Effects
    // #endregion
    // #region Event handlers
    /**
     * Handle verification submit clicked action. Validates the verification code and calls the appropriate
     * mutation based on the context (creating vendor contact, updating vendor contact, or verifying email).
     * @param verificationCode String array of verification code
     */
    const handleVerificationSubmitClicked = (verificationCode) => __awaiter(void 0, void 0, void 0, function* () {
        setErrorMessages(errorMessages => []);
        setLoading(true);
        if (verificationCode.some(code => code === '' || isNaN(Number(code)))) {
            setErrorMessages(errorMessages => [{ message: intl.formatMessage(messages.contact_verification___code_error) }]);
            setLoading(false);
        }
        else {
            //If we are creating a vendor contact
            if (props.vendorContact.id == "" || props.vendorContact.id == undefined || props.vendorContact.id == -1) {
                handleCreateVendorContact(verificationCode);
            }
            else if (props.oldVendorContact.id === props.vendorContact.id && props.oldVendorContact != props.vendorContact) {
                //If we are updating vendor contact
                handleUpdateVendorContact(verificationCode);
            }
            else {
                //If we are verifying email
                handleVerifyEmail(verificationCode);
            }
        }
    });
    /**
     * Handles successful verification of the vendor contact. If creating a PO, it dispatches an action to create the PO and assign the vendor contact to it.
     * Otherwise, it closes the verification modal.
     * @param vendorContactId Vendor Contact ID
     */
    const handleSuccessfulVerification = (vendorContactId) => {
        var _a;
        //If code is verified, check if we are creating a PO
        if (props.isCreatingPo) {
            // Track successful PO creation event
            event({
                action: 'create_po',
                category: 'PO',
                label: 'Create PO',
                value: props.vehicleId
            });
            //then create po and assign contact to it
            props.dispatch({ type: constants.CREATE_PO_CLICKED, vehicleId: props.vehicleId, assignVendorContactToOrder: assignVendorContactToOrderMutation, vendorContactId: vendorContactId, onCloseModalCallback: onCloseModal });
        }
        else {
            //If not creating a PO, call handleVerificationModalClose and close the modal
            (_a = props.handleVerificationModalClose()) === null || _a === void 0 ? void 0 : _a.then(() => {
                onCloseModal();
            });
        }
    };
    const onCloseModal = () => {
        onCleanUp();
        props.toggleShowModal();
    };
    const onCleanUp = () => {
        setLoading(false);
        setErrorMessages([]);
        setVerificationCode(['', '', '', '']);
    };
    /**
     *
     * @param event Input change event
     * @param ref Input ref
     * @param nextRef Next sequential input ref
     * @param index Input index
     * @description Handles input change event for the verification code inputs. Updates the verification code state and focuses on the next input. Submits the code if the last input is filled.
     */
    const onInputChanged = (event, ref, nextRef, index) => {
        if (event.target.value !== "" && event.target.value !== undefined) {
            const newVerificationCode = [...verificationCode.slice(0, index), event.target.value, ...verificationCode.slice(index + 1)];
            setVerificationCode(newVerificationCode);
            if (ref.current !== input4.current) {
                nextRef.current.focus();
                event.preventDefault(); // Prevent form submission
            }
            else if (ref.current === input4.current) {
                handleVerificationSubmitClicked(newVerificationCode);
            }
        }
    };
    /**
     *
     * @param event Input key up event
     * @param ref Input ref
     * @param nextRef Next sequential input ref
     * @param prevRef Previous sequential input ref
     * @description Handles key up event for the verification code inputs. Moves focus to the next or previous input based on the arrow keys pressed.
     */
    const onInputKeyUp = (event, ref, nextRef, prevRef) => {
        switch (event.key) {
            case 'ArrowLeft':
                if (ref.current !== input1.current) {
                    prevRef.current.focus();
                    event.preventDefault(); // Prevent form submission
                }
                break;
            case 'ArrowRight':
                if (ref.current !== input4.current) {
                    nextRef.current.focus();
                    event.preventDefault(); // Prevent form submission
                }
                break;
        }
    };
    /**
     *
     * @param event Input paste event
     * @description Handles paste event for the verification code inputs. If the pasted data is a valid 4-digit code, it updates the verification code state and submits the code.
     */
    const onInputPaste = (event) => {
        event.preventDefault();
        const clipboardData = event.clipboardData;
        const pastedData = clipboardData.getData('Text');
        if (pastedData.length === 4 && !isNaN(Number(pastedData))) {
            setVerificationCode(pastedData.split(''));
            handleVerificationSubmitClicked(pastedData.split(''));
        }
    };
    /**
     * @description Handles the resend verification code button click event. If the resend is disabled, it shows a toast message. Otherwise, it resets the verification code and calls the resend verification email function.
     */
    const resendVerificationClicked = () => {
        setErrorMessages([]);
        if (props.resendDisabled) {
            setToastMessage(messages.contact_verification___resend_disabled);
            setShowToast(true);
        }
        else {
            setVerificationCode(['', '', '', '']);
            props.resendVerificationEmail(props.vendorContact);
            setToastMessage(messages.contact_verification___resent_code);
            setShowToast(true);
        }
    };
    /**
     *
     * @param verificationCode Verification code
     * @description Handles the update vendor contact mutation. If the mutation is successful, it calls the handleSuccessfulVerification function. Otherwise, it sets the error messages based on the mutation result.
     */
    const handleUpdateVendorContact = (verificationCode) => __awaiter(void 0, void 0, void 0, function* () {
        yield updateAndVerifyVendorApprovalContactMutation({
            variables: {
                vendorContactId: props.vendorContact.id,
                vendorContactVerifyId: props.verificationId,
                pin: verificationCode.join(''),
                emailAddress: props.vendorContact.emailAddress,
                phoneNumber: props.vendorContact.phoneNumber,
                extension: props.vendorContact.extension,
                name: props.vendorContact.name,
                errorCodeLanguage: props.userLocale
            }
        }).then((result) => {
            if (result.data.updateAndVerifyVendorApprovalContact.verificationPayload.success) {
                handleSuccessfulVerification(props.vendorContact.id);
            }
            else {
                if (result.data.updateAndVerifyVendorApprovalContact.verificationPayload.errorCode.startsWith("VS"))
                    setErrorMessages(errorMessages => [{ message: result.data.updateAndVerifyVendorApprovalContact.verificationPayload.message }]);
                else
                    setErrorMessages(errorMessages => helperFunctions.getGenericErrorObject());
            }
        }).finally(() => {
            setLoading(false);
        });
    });
    /**
     *
     * @param verificationCode Verification code
     * @description Handles the create vendor contact mutation. If the mutation is successful, it calls the handleSuccessfulVerification function. Otherwise, it sets the error messages based on the mutation result.
     */
    const handleCreateVendorContact = (verificationCode) => __awaiter(void 0, void 0, void 0, function* () {
        yield createAndVerifyVendorApprovalContactMutation({
            variables: {
                vendorContactVerifyId: props.verificationId,
                pin: verificationCode.join(''),
                emailAddress: props.vendorContact.emailAddress,
                phoneNumber: props.vendorContact.phoneNumber,
                extension: props.vendorContact.extension,
                name: props.vendorContact.name,
                errorCodeLanguage: props.userLocale,
                vendorId: props.vendorContact.vendorId
            }
        }).then((result) => {
            if (result.data.createAndVerifyVendorApprovalContact.createContactPayload.success) {
                handleSuccessfulVerification(result.data.createAndVerifyVendorApprovalContact.createContactPayload.vendorContactId);
            }
            else {
                if (result.data.createAndVerifyVendorApprovalContact.createContactPayload.errorCode.startsWith("VS"))
                    setErrorMessages(errorMessages => [{ message: result.data.createAndVerifyVendorApprovalContact.createContactPayload.message }]);
                else
                    setErrorMessages(errorMessages => helperFunctions.getGenericErrorObject());
            }
        }).finally(() => {
            setLoading(false);
        });
    });
    /**
     *
     * @param verificationCode Verification code
     * @description Handles the verify email mutation. If the mutation is successful, it calls the handleSuccessfulVerification function. Otherwise, it sets the error messages based on the mutation result.
     */
    const handleVerifyEmail = (verificationCode) => __awaiter(void 0, void 0, void 0, function* () {
        yield verifyContactEmailMutation({
            variables: {
                vendorContactId: props.vendorContact.id,
                vendorContactVerifyId: props.verificationId,
                pin: verificationCode.join(''),
                language: props.userLocale
            }
        }).then((result) => {
            if (result.data.verifyContactEmail.verificationPayload.success) {
                handleSuccessfulVerification(props.vendorContact.id);
            }
            else {
                if (result.data.verifyContactEmail.verificationPayload.errorCode.startsWith("VS"))
                    setErrorMessages(errorMessages => [{ message: result.data.verifyContactEmail.verificationPayload.message }]);
                else
                    setErrorMessages(errorMessages => helperFunctions.getGenericErrorObject());
            }
        }).finally(() => {
            setLoading(false);
        });
    });
    // #endregion
    // #region International messages
    const messages = defineMessages({
        contact_verification__email_title: {
            id: "contact_verification__email_title",
            description: "Email Verification",
            defaultMessage: "Email Verification"
        },
        cancel: {
            id: "cancel",
            description: "Cancel",
            defaultMessage: "Cancel"
        },
        contact_verification___email_body_1: {
            id: "contact_verification___email_body_1",
            description: "<p>We have sent a 4-digit verification code to {email}.</p>",
            defaultMessage: "<p>We have sent a 4-digit verification code to {email}.</p>"
        },
        contact_verification___email_body_2: {
            id: "contact_verification___email_body_2",
            description: "Please check your inbox and enter the code below to confirm your email.",
            defaultMessage: "Please check your inbox and enter the code below to confirm your email."
        },
        contact_verification___email_body_3: {
            id: "contact_verification___email_body_3",
            description: "Email may take a few minutes to arrive. If you don't see it, check your spam folder.",
            defaultMessage: "Email may take a few minutes to arrive. If you don't see it, check your spam folder."
        },
        contact_verification___resend_code: {
            id: "contact_verification___resend_code",
            description: "Click here to resend verification code",
            defaultMessage: "Click here to resend verification code"
        },
        contact_verification___submit_and_continue: {
            id: "contact_verification___submit",
            description: "Verify and continue to next step",
            defaultMessage: "Verify and continue to next step"
        },
        contact_verification___submit: {
            id: "contact_verification___submit",
            description: "Verify email",
            defaultMessage: "Verify email"
        },
        contact_verification___code_error: {
            id: "contact_verification___code_error",
            description: "Please enter a valid verification code.",
            defaultMessage: "Please enter a valid verification code."
        },
        contact_verification___resend_disabled: {
            id: "contact_verification___resend_disabled",
            description: "Please wait one minute before resending code",
            defaultMessage: "Please wait one minute before resending code"
        },
        contact_verification___resent_code: {
            id: "contact_verification___resent_code",
            description: "Verification code resent",
            defaultMessage: "Verification code resent"
        },
    });
    // #endregion
    // #region Render helpers
    const renderCloseButton = () => {
        return (React.createElement(ModalClose, { role: 'button', id: "close_btn", onClick: () => onCloseModal(), style: { width: "fit-content", marginBottom: "0.5rem" } },
            React.createElement("img", { src: "/assets/VMS_33button_kill_blue.png" }),
            "  ",
            " ",
            React.createElement(FormattedMessage, Object.assign({}, messages.cancel))));
    };
    // #endregion  
    // #region Render
    return (React.createElement(ModalStyled, { show: props.showModal, backdrop: "static", keyboard: false, style: { overflow: "auto" } },
        React.createElement(ModalBody, null,
            renderCloseButton(),
            React.createElement(FormWrapper, { id: "contactVerificationErrorMessages", key: "contactVerificationErrorMessages", formErrors: [...errorMessages, ...((_a = props.createPoError) === null || _a === void 0 ? void 0 : _a.some(e => e.message)) ? props.createPoError : []] }),
            React.createElement(ResendToast, { show: showToast, onClose: () => { setShowToast(false); setToastMessage(null); }, delay: 5000, autohide: true },
                React.createElement(ResendToast.Body, null, toastMessage && React.createElement(FormattedMessage, Object.assign({}, toastMessage)))),
            React.createElement(ModalContent, null,
                React.createElement(Col, { md: 1 },
                    React.createElement(Line, null)),
                React.createElement(Col, null,
                    React.createElement(ModalMainHeader, null,
                        React.createElement(FormattedMessage, Object.assign({}, messages.contact_verification__email_title))),
                    React.createElement(Row, { className: "justify-content-center", style: { padding: "5rem 0 3rem 0" } },
                        React.createElement(Row, { lg: 8 },
                            React.createElement(FormattedMessage, Object.assign({}, messages.contact_verification___email_body_1, { values: {
                                    email: (React.createElement("u", { style: { fontWeight: 600 } }, props.vendorContact.emailAddress)),
                                    p: (...chunks) => React.createElement("p", { style: { padding: 0, margin: 0 } }, chunks)
                                } }))),
                        React.createElement(Row, { lg: 8 },
                            React.createElement(FormattedMessage, Object.assign({}, messages.contact_verification___email_body_2))),
                        React.createElement(Row, { lg: 8 },
                            React.createElement(FormattedMessage, Object.assign({}, messages.contact_verification___email_body_3)))),
                    React.createElement(Row, { className: "justify-content-start" },
                        React.createElement(InputCol, { md: 1 },
                            React.createElement(CodeInput, { className: "verification_pin_input", format: "#", placeholder: "-", value: verificationCode[0], id: "contact_verification_code_1", autoComplete: "off", onChange: (e) => onInputChanged(e, input1, input2, 0), onKeyUp: (e) => onInputKeyUp(e, input1, input2, null), maxLength: 1, getInputRef: input1, type: 'text', onPaste: onInputPaste, readOnly: loading, loading: loading })),
                        React.createElement(InputCol, { md: 1 },
                            React.createElement(CodeInput, { className: "verification_pin_input", format: "#", placeholder: "-", value: verificationCode[1], id: "contact_verification_code_2", autoComplete: "off", onChange: (e) => { onInputChanged(e, input2, input3, 1); }, onKeyUp: (e) => onInputKeyUp(e, input2, input3, input1), maxLength: 1, getInputRef: input2, type: 'text', onPaste: onInputPaste, readOnly: loading, loading: loading })),
                        React.createElement(InputCol, { md: 1 },
                            React.createElement(CodeInput, { className: "verification_pin_input", format: "#", placeholder: "-", value: verificationCode[2], id: "contact_verification_code_3", autoComplete: "off", onChange: (e) => { onInputChanged(e, input3, input4, 2); }, onKeyUp: (e) => onInputKeyUp(e, input3, input4, input2), maxLength: 1, getInputRef: input3, type: 'text', onPaste: onInputPaste, readOnly: loading, loading: loading })),
                        React.createElement(InputCol, { md: 1 },
                            React.createElement(CodeInput, { className: "verification_pin_input", format: "#", placeholder: "-", value: verificationCode[3], id: "contact_verification_code_4", autoComplete: "off", onChange: (e) => { onInputChanged(e, input4, null, 3); }, onKeyUp: (e) => onInputKeyUp(e, input4, null, input3), maxLength: 1, getInputRef: input4, type: 'text', onPaste: onInputPaste, readOnly: loading, loading: loading }))),
                    React.createElement(Row, { className: "justify-content-start", style: { paddingTop: "1rem", alignItems: "center" } },
                        React.createElement(Col, { md: 6 },
                            React.createElement(ButtonStyled, { role: "button", onClick: resendVerificationClicked, style: { width: "fit-content", paddingRight: "0" }, id: "contact_verification_resend_btn", disable: loading },
                                React.createElement("img", { src: "/assets/VMS_28button_edit_clear.png" }),
                                ' ',
                                React.createElement(FormattedMessage, Object.assign({}, messages.contact_verification___resend_code))))),
                    React.createElement(Row, { className: "justify-content-end align-items-center", style: { paddingTop: "3.5rem" } },
                        React.createElement(ButtonStyled, { role: "button", onClick: () => handleVerificationSubmitClicked(verificationCode), style: { width: "fit-content", paddingRight: "0" }, id: "contact_verification_submit_btn", disable: loading },
                            props.isCreatingPo ?
                                React.createElement(FormattedMessage, Object.assign({}, messages.contact_verification___submit_and_continue))
                                : React.createElement(FormattedMessage, Object.assign({}, messages.contact_verification___submit)),
                            " ",
                            React.createElement("img", { src: "/assets/VMS_33button_go_blue.png" })),
                        loading && React.createElement(Spinner, { style: { height: "23px" }, animation: 'border' })))))));
    // #endregion
};
// #endregion
const mapStateToProps = (state, props) => {
    return {
        createPoError: state.appState.getIn(['serverData', 'shared', 'errorMessage'])
    };
};
// #endregion
export default connect(mapStateToProps)(ContactVerificationModal);
